import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import StyledMainMenuRebud from './MainMenuRebud.styled';
import { featuredBrandPT, categoriesPT, personalityPT, discoversAllPT } from '../../../config/prop-types';
import { locationActions } from '../../../actions';

const thc = [
  { id: '1', name: 'Low THC', uri_param: 'lowTHC' },
  { id: '2', name: 'Medium THC', uri_param: 'mediumTHC' },
  { id: '3', name: 'Highest THC', uri_param: 'highestTHC' },
];

const resources = [
  { name: 'About Us', to: '/about' },
  { name: 'Blog', to: '/blog' },
  { name: 'Contact Us', to: '/contact' },
];

const MainMenuRebud = React.memo(({ categories, featuredBrand, personality, discoversAll, setMenuOpen }) => {
  const [active, setActive] = useState(null);
  const closeTimer = useRef(null);
  const itemRefs = useRef({});
  const [dispensaries, setDispensaries] = useState([]);

  useEffect(() => {
    locationActions
      .getPickupLocationsList()
      .then((data) => {
        if (data?.locations?.length > 0) {
          setDispensaries(data.locations.map((item) => ({ uri_param: item.uri_param, name: item.name })));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allBrands = featuredBrand?.[0]?.brands || [];
  const brandCols = (() => {
    if (!allBrands.length) return [];
    const numCols = 4;
    const perCol = Math.ceil(allBrands.length / numCols);
    return Array.from({ length: numCols }, (_, i) => allBrands.slice(i * perCol, (i + 1) * perCol)).filter(
      (col) => col.length > 0
    );
  })();

  const open = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActive(null), 150);
  };

  const handleLinkClick = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(null);
    setMenuOpen && setMenuOpen(false);
  };

  const getDropdownStyle = (key) => {
    const el = itemRefs.current[key];
    if (!el || typeof window === 'undefined') return { left: 0 };
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const spaceToLeft = rect.left + 8;
    // if (spaceToRight >= minWidth) {
    //   return { left: 0, right: 'auto', width: Math.min(spaceToRight, vw - 16) + 'px' };
    // }
    // const spaceToLeft = rect.right - 8;
    return { maxWidth: `calc(100vw - ${spaceToLeft}px)` };
  };

  return (
    <StyledMainMenuRebud>
      <nav className="rmenu" aria-label="Main navigation">
        <ul className="rmenu__list">
          {/* SHOP — mega menu */}
          <li
            ref={(el) => (itemRefs.current['shop'] = el)}
            className="rmenu__item rmenu__item--shop"
            onMouseEnter={() => open('shop')}
            onMouseLeave={scheduleClose}
          >
            <button className={`rmenu__trigger${active === 'shop' ? ' rmenu__trigger--open' : ''}`} type="button">
              SHOP <FaChevronDown className="rmenu__chevron" />
            </button>

            {active === 'shop' && (
              <div className="rmenu__mega" style={getDropdownStyle('shop')}>
                <div className="rmenu__mega-inner">
                  <div className="rmenu__col">
                    <h3 className="rmenu__col-heading">BY CATEGORY</h3>
                    <ul className="rmenu__col-list">
                      {categories?.slice(0, 9).map((cat, i) => (
                        <li key={i}>
                          <Link to={`/category/${cat.uri_param}`} onClick={handleLinkClick} className="rmenu__link">
                            {cat.name}
                          </Link>
                        </li>
                      ))}
                      <li>
                        <Link to="/shop" onClick={handleLinkClick} className="rmenu__link rmenu__link--shop-all">
                          Shop All
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div className="rmenu__col">
                    <h3 className="rmenu__col-heading">BY PERSONALITY</h3>
                    <ul className="rmenu__col-list">
                      {personality?.map((item, i) => (
                        <li key={i}>
                          <Link
                            to={`/shop?by_personality=${item.uri_param}`}
                            onClick={handleLinkClick}
                            className="rmenu__link"
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rmenu__col">
                    <h3 className="rmenu__col-heading">BY POTENCY</h3>
                    <ul className="rmenu__col-list">
                      {thc.map((item, i) => (
                        <li key={i}>
                          <Link to={`/shop?thc=${item.uri_param}`} onClick={handleLinkClick} className="rmenu__link">
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rmenu__col">
                    <h3 className="rmenu__col-heading">DISCOVER</h3>
                    <ul className="rmenu__col-list">
                      {discoversAll?.map((item, i) => (
                        <li key={i}>
                          <Link
                            to={`/shop?discover=${item.uri_param}`}
                            onClick={handleLinkClick}
                            className="rmenu__link"
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </li>

          {/* DEALS */}
          <li className="rmenu__item">
            <Link to="/promotions" className="rmenu__trigger rmenu__trigger--link" onClick={handleLinkClick}>
              DEALS
            </Link>
          </li>

          {/* DISPENSARIES */}
          <li
            ref={(el) => (itemRefs.current['dispensaries'] = el)}
            className="rmenu__item rmenu__item--dropdown"
            onMouseEnter={() => open('dispensaries')}
            onMouseLeave={scheduleClose}
          >
            <button
              className={`rmenu__trigger${active === 'dispensaries' ? ' rmenu__trigger--open' : ''}`}
              type="button"
            >
              DISPENSARIES <FaChevronDown className="rmenu__chevron" />
            </button>
            {active === 'dispensaries' && dispensaries.length > 0 && (
              <div className="rmenu__dropdown" style={getDropdownStyle('dispensaries')}>
                {dispensaries.map((loc, i) => (
                  <Link
                    key={i}
                    to={`/dispensary/${loc.uri_param}`}
                    onClick={handleLinkClick}
                    className="rmenu__dropdown-item"
                  >
                    {loc.name}
                  </Link>
                ))}
              </div>
            )}
          </li>

          {/* DELIVERY */}
          <li className="rmenu__item">
            <Link to="/weed-delivery" className="rmenu__trigger rmenu__trigger--link" onClick={handleLinkClick}>
              DELIVERY
            </Link>
          </li>

          {/* BRANDS */}
          {allBrands.length > 0 && (
            <li
              ref={(el) => (itemRefs.current['brands'] = el)}
              className="rmenu__item rmenu__item--dropdown"
              onMouseEnter={() => open('brands')}
              onMouseLeave={scheduleClose}
            >
              <button className={`rmenu__trigger${active === 'brands' ? ' rmenu__trigger--open' : ''}`} type="button">
                BRANDS <FaChevronDown className="rmenu__chevron" />
              </button>
              {active === 'brands' && (
                <div className="rmenu__mega" style={getDropdownStyle('brands')}>
                  <div className="rmenu__mega-inner">
                    {brandCols.map((col, ci) => (
                      <div key={ci} className="rmenu__col">
                        <ul className="rmenu__col-list">
                          {col.map((brand, i) => (
                            <li key={i}>
                              <Link to={`/brands/${brand.uri_param}`} onClick={handleLinkClick} className="rmenu__link">
                                {brand.name}
                              </Link>
                            </li>
                          ))}
                          {ci === 0 && (
                            <li>
                              <Link
                                to="/brands"
                                onClick={handleLinkClick}
                                className="rmenu__link rmenu__link--shop-all"
                              >
                                All Brands
                              </Link>
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </li>
          )}

          {/* RESOURCES */}
          <li
            ref={(el) => (itemRefs.current['resources'] = el)}
            className="rmenu__item rmenu__item--dropdown"
            onMouseEnter={() => open('resources')}
            onMouseLeave={scheduleClose}
          >
            <button className={`rmenu__trigger${active === 'resources' ? ' rmenu__trigger--open' : ''}`} type="button">
              RESOURCES <FaChevronDown className="rmenu__chevron" />
            </button>
            {active === 'resources' && (
              <div className="rmenu__dropdown" style={getDropdownStyle('resources')}>
                {resources.map((item, i) => (
                  <Link key={i} to={item.to} onClick={handleLinkClick} className="rmenu__dropdown-item">
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </li>
        </ul>
      </nav>
    </StyledMainMenuRebud>
  );
});

MainMenuRebud.propTypes = {
  categories: categoriesPT,
  featuredBrand: featuredBrandPT,
  personality: personalityPT,
  discoversAll: discoversAllPT,
  setMenuOpen: PropTypes.func,
};

export default MainMenuRebud;
